import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Heart, Gamepad2, Maximize2, RotateCcw, Trophy, Send, WifiOff, Play, Coins } from 'lucide-react';
import { usePlayer } from './PlayerAuth';
import { usePlatform } from '../BrandProvider';
import { MarkdownText } from '../LegalPage';
import { ProfileButton } from './AuthDialog';
import { CharacterArt } from './Profile';
import { toast } from 'sonner';
import { storeApi, clientId, playerApi } from '../../lib/api';
import { useLocalProgress } from '../../lib/progress';
import { buildGameDoc, loadRuntime } from '../../lib/runtime';
import { bus, vibrate } from '../../lib/bus';
import { watchGamepads, padLabel } from '../../lib/gamepad';
import { db } from '../../lib/db';

function Leaderboard({ scores, highlight }) {
  return (
    <div data-testid="store-leaderboard" className="space-y-1">
      {scores.length === 0 && <div className="text-xs text-slate-500 t-chat">Nessun record: sii il primo!</div>}
      {scores.map((s, i) => (
        <div key={s.id} className={`flex items-center gap-3 rounded-lg px-3 py-2 text-xs ${s.id === highlight ? 'bg-cyan-500/15 border border-cyan-400/40' : i < 3 ? 'bg-white/[.03]' : ''}`}>
          <span className={`w-6 font-bold ${i === 0 ? 'text-amber-300' : i === 1 ? 'text-slate-300' : i === 2 ? 'text-orange-400' : 'text-slate-500'}`}>#{i + 1}</span>
          <span className="flex-1 truncate text-slate-200">{s.nickname}</span>
          <span className="font-bold text-cyan-300 tabular-nums">{s.score.toLocaleString('it-IT')}</span>
        </div>
      ))}
    </div>
  );
}

function ScoreSubmit({ score, onSubmit, onClose, onLogin }) {
  const [nick, setNick] = useState(localStorage.getItem('titan.nick') || '');
  return (
    <div data-testid="score-submit" className="absolute inset-x-3 bottom-3 sm:left-auto sm:right-3 sm:w-80 z-20 glass rounded-2xl p-4 rise">
      <div className="text-xs text-slate-300">Partita finita: <b className="text-cyan-300 text-base">{score.toLocaleString('it-IT')}</b></div>
      <button data-testid="score-login-btn" onClick={onLogin} className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-300 hover:text-amber-200"><Coins className="h-3.5 w-3.5" />Accedi per guadagnare monete e salvare il record</button>
      <div className="flex gap-2 mt-3">
        <input data-testid="score-nick-input" value={nick} maxLength={16} onChange={(e) => setNick(e.target.value)} placeholder="Il tuo nickname"
          className="flex-1 rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-xs outline-none focus:border-cyan-400/60" />
        <button data-testid="score-submit-btn" disabled={!nick.trim()} onClick={() => { localStorage.setItem('titan.nick', nick.trim()); onSubmit(nick.trim()); }}
          className="rounded-lg bg-violet-600 px-3 disabled:opacity-40"><Send className="h-4 w-4" /></button>
      </div>
      <button data-testid="score-skip-btn" onClick={onClose} className="mt-2 text-[10px] text-slate-500">Non salvare</button>
    </div>
  );
}

export default function StorePlayer() {
  const { id: param } = useParams();
  const [gameId, setGameId] = useState(null);
  const id = gameId || param;
  const [game, setGame] = useState(null);
  const [err, setErr] = useState(null);
  const [offline, setOffline] = useState(false);
  const [ready, setReady] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [plays, setPlays] = useState(0);
  const [scores, setScores] = useState([]);
  const [pending, setPending] = useState(null);
  const [mine, setMine] = useState(null);
  const [reload, setReload] = useState(0);
  const [pads, setPads] = useState([]);
  const wrap = useRef(null);
  const frame = useRef(null);
  const cid = clientId();
  const { user, setUser, character, setAuthOpen } = usePlayer();
  const { features } = usePlatform();
  const [started, setStarted] = useState(false);
  const [runCoins, setRunCoins] = useState(0);
  const [reward, setReward] = useState(null);
  const run = useRef(null);
  const newRun = () => { run.current = playerApi.startRun(id).then((r) => r.run_id).catch(() => null); return run.current; };
  useEffect(() => { if (user && started && !run.current) newRun(); }, [user?.user_id, started]); // eslint-disable-line

  useEffect(() => watchGamepads(setPads), []);
  useEffect(() => {
    let alive = true;
    loadRuntime().then(() => alive && setReady(true));
    storeApi.get(param).then((g) => {
      if (!alive) return;
      setGameId(g.id); setGame(g); setLikes(g.likes); setPlays(g.plays);
      db.kvSet(`storeGame:${param}`, g);
      storeApi.play(g.id).then((r) => alive && setPlays(r.plays)).catch(() => {});
      storeApi.liked(g.id, cid).then((r) => alive && setLiked(r.liked)).catch(() => {});
      storeApi.scores(g.id).then((r) => alive && setScores(r)).catch(() => {});
    }).catch(async (e) => {
      const cached = await db.kvGet(`storeGame:${param}`);
      if (!alive) return;
      if (cached && !e.status) { setGameId(cached.id); setGame(cached); setLikes(cached.likes); setPlays(cached.plays); setOffline(true); } else setErr(e.message);
    });
    return () => { alive = false; };
  }, [param]); // eslint-disable-line

  useEffect(() => bus.on((m) => {
    if (m.type === 'haptic') vibrate(m.p);
    else if (m.type === 'sdk' && m.action === 'login') { if (!user) setAuthOpen(true); }
    else if (m.type === 'sdk' && m.action === 'shop') toast('Negozio in arrivo', { id: 'sdk-shop', description: 'Gli acquisti nei giochi non sono ancora disponibili.' });
    else if (m.type === 'coins') setRunCoins(m.total);
    else if (m.type === 'gameover' && m.value > 0) { setPending({ score: m.value, coins: m.coins || 0 }); setRunCoins(0); }
  }), [user, setAuthOpen]);

  useEffect(() => {
    if (!pending || !user) return;
    const p = pending; setPending(null);
    const getRun = async () => { let rid = run.current && await run.current; if (!rid) { rid = await newRun(); await new Promise((ok) => setTimeout(ok, 2100)); } run.current = null; return rid; };
    getRun().then((rid) => playerApi.rewards(id, p.score, p.coins, rid)).then(async (r) => {
      newRun();
      setUser(r.user); setReward(r); setMine(null);
      setScores(await storeApi.scores(id));
      toast.success(`+${r.earned} monete${r.new_best ? ' · nuovo record personale!' : ''} · #${r.rank} in classifica`);
      setTimeout(() => setReward(null), 4000);
    }).catch((e) => { newRun(); setReward({ error: e.message }); setTimeout(() => setReward(null), 3500); });
  }, [pending, user]); // eslint-disable-line

  const progress = useLocalProgress(gameId ? `store:${gameId}` : null);
  const charCfg = character ? { id: character.id, name: character.name, primary: character.primary, secondary: character.secondary, trail: character.trail } : undefined;
  const srcDoc = useMemo(() => (game && ready && progress.loaded ? buildGameDoc(game.code, { fps: 60, controls: game.controls || 'dpad', showPad: 'auto', master: 0.8, sfx: 0.8, music: 0.35, bpm: 120, musicOn: false, haptics: true, character: charCfg, storage: progress.get(), player: user ? { state: 'user', name: user.name, coins: user.coins || 0, characters: user.owned_characters || [] } : undefined }) : null), [game, ready, character?.id, progress.loaded, reload]); // eslint-disable-line

  const toggleLike = async () => {
    vibrate(12);
    try { const r = await storeApi.like(id, cid); setLiked(r.liked); setLikes(r.likes); } catch (e) { toast.error('Serve la connessione per mettere Mi piace'); }
  };
  const submit = async (nick) => {
    const score = pending.score; setPending(null);
    try {
      const r = await storeApi.addScore(id, nick, score);
      setMine(r.id); setScores(await storeApi.scores(id));
      toast.success(`Record salvato: posizione #${r.rank}`);
    } catch (e) { toast.error(e.message); }
  };
  const fullscreen = async () => { try { await wrap.current.requestFullscreen(); } catch { /* iOS */ } frame.current?.focus(); };
  const start = () => { setStarted(true); vibrate(12); fullscreen(); };

  if (err) return (
    <div className="titan-root min-h-[100dvh] flex items-center justify-center p-8 text-center">
      <div className="glass rounded-2xl p-10"><div className="text-lg font-bold text-slate-100">Gioco non disponibile</div><p className="text-sm text-slate-400 mt-2 t-chat">{err}</p>
        <Link to="/" data-testid="player-back-store" className="inline-block mt-6 rounded-full bg-violet-600 px-5 py-2 text-sm font-bold">Torna allo Store</Link></div>
    </div>
  );

  return (
    <div data-testid="store-player" className="titan-root min-h-[100dvh] lg:h-[100dvh] flex flex-col lg:overflow-hidden">
      <header className="relative z-20 pt-safe border-b border-violet-500/15 bg-titan-bg/80 backdrop-blur-xl">
        <div className="h-14 px-3 sm:px-5 flex items-center gap-3">
          <Link to="/" data-testid="player-back" className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-300 hover:bg-white/5"><ArrowLeft className="h-5 w-5" /></Link>
          <div className="min-w-0">
            <div data-testid="player-title" className="text-sm font-bold text-slate-100 truncate">{game?.title || 'Caricamento…'}</div>
            <div className="t-label">{game?.genre}</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {pads.length > 0 && <span className="hidden sm:flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-[10px] text-emerald-300"><Gamepad2 className="h-3 w-3" />{padLabel(pads[0])}</span>}
            <ProfileButton />
            <button data-testid="player-like-btn" onClick={toggleLike} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs border transition-colors ${liked ? 'bg-rose-500/20 border-rose-400 text-rose-200' : 'border-white/15 text-slate-300 hover:border-rose-400/60'}`}>
              <Heart className={`h-4 w-4 ${liked ? 'fill-rose-400 text-rose-400' : ''}`} /><span data-testid="player-likes">{likes}</span>
            </button>
            <button data-testid="player-reload-btn" onClick={() => setReload((r) => r + 1)} className="hidden sm:flex h-9 w-9 rounded-lg items-center justify-center text-slate-300 hover:bg-white/5"><RotateCcw className="h-4 w-4" /></button>
            <button data-testid="player-fullscreen-btn" onClick={fullscreen} className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-300 hover:bg-white/5"><Maximize2 className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <div className="relative z-10 flex-1 min-h-0 flex flex-col lg:flex-row gap-3 lg:p-3">
        <div ref={wrap} className="relative h-[72dvh] lg:h-auto lg:flex-1 bg-black lg:rounded-2xl overflow-hidden border-violet-500/20 lg:border">
          {srcDoc && <iframe key={reload} ref={(el) => { frame.current = el; if (el) bus.attach(el); }} title={game.title} data-testid="player-iframe" srcDoc={srcDoc}
            sandbox="allow-scripts allow-pointer-lock" allow="autoplay; fullscreen; gamepad" onLoad={() => frame.current?.focus()} className="absolute inset-0 h-full w-full border-0" />}
          {pending !== null && !user && <ScoreSubmit score={pending.score} onSubmit={submit} onClose={() => setPending(null)} onLogin={() => setAuthOpen(true)} />}
          {started && runCoins > 0 && <div data-testid="run-coins" className="pointer-events-none absolute top-3 right-3 z-10 flex items-center gap-1 rounded-full bg-black/60 px-3 py-1 text-xs font-bold text-amber-300 backdrop-blur"><Coins className="h-3.5 w-3.5" />+{runCoins}</div>}
          {reward && <div data-testid="reward-toast" className="pointer-events-none absolute inset-x-0 top-1/4 z-20 flex justify-center rise"><div className="glass rounded-2xl px-6 py-4 text-center">{reward.error ? <div className="text-sm text-rose-300">{reward.error}</div> : <><div className="text-3xl font-extrabold text-amber-300 flex items-center gap-2 justify-center"><Coins className="h-7 w-7" />+{reward.earned}</div><div className="text-xs text-slate-300 mt-1">Saldo {reward.coins} monete{reward.new_best ? ' · nuovo record!' : ''} · #{reward.rank}</div></>}</div></div>}
          {!started && srcDoc && (
            <div data-testid="play-overlay" className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-5 bg-titan-bg/75 backdrop-blur-sm">
              {character && <CharacterArt c={character} size={72} />}
              <button data-testid="play-now-btn" onClick={start} className="neon-pulse flex items-center gap-2 rounded-full px-8 py-4 text-base font-extrabold tracking-wider bg-gradient-to-r from-violet-600 to-cyan-600"><Play className="h-5 w-5" />GIOCA ORA</button>
              <span className="text-[11px] text-slate-400">Schermo intero · {user ? `giochi come ${character?.name || 'Nova'}` : 'accedi per guadagnare monete'}</span>
              <button data-testid="play-windowed-btn" onClick={() => { setStarted(true); frame.current?.focus(); }} className="text-[11px] text-slate-500 hover:text-slate-300">oppure gioca in finestra</button>
            </div>
          )}
        </div>
        <aside className="lg:w-[340px] glass lg:rounded-2xl p-5 space-y-5 lg:overflow-y-auto">
          {offline && <div className="flex items-center gap-2 text-xs text-amber-300"><WifiOff className="h-4 w-4" />Offline: versione salvata</div>}
          <div data-testid="player-progress-local" className="text-xs text-slate-300 t-chat">{user ? 'Progressi del gioco salvati su questo dispositivo · monete e record sul tuo account' : 'Giochi come ospite: i progressi restano solo su questo dispositivo'}</div>
          {game?.subtitle && <p className="text-xs text-cyan-200 mb-1">{game.subtitle}</p>}
          <p className="text-sm text-slate-300 t-chat">{game?.description || 'Gioca con touch, tastiera (frecce/WASD + Spazio) o controller.'}</p>
          {game?.long_description && <div data-testid="player-long-desc" className="mt-3 text-sm text-slate-300 t-chat"><MarkdownText src={game.long_description} /></div>}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/10 p-3"><div className="t-label">Partite</div><div data-testid="player-plays" className="text-xl font-bold text-cyan-300 mt-1">{plays}</div></div>
            <div className="rounded-xl border border-white/10 p-3"><div className="t-label">Mi piace</div><div className="text-xl font-bold text-rose-300 mt-1">{likes}</div></div>
          </div>
          <div>
            {features.enableGlobalLeaderboard && <>
            <div className="flex items-center gap-2 mb-3"><Trophy className="h-4 w-4 text-amber-300" /><span className="text-xs font-bold tracking-wider text-slate-100">CLASSIFICA GLOBALE</span></div>
            <Leaderboard scores={scores} highlight={mine} />
            </>}
          </div>
        </aside>
      </div>
    </div>
  );
}
